import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
import { Card, CardHeader, CardBody } from '../components/Card';
import { Alert } from '../components/Alert';
import { serviceAPI } from '../api/serviceAPI';
import { regionAPI } from '../api/regionAPI';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Avatar } from '../components/Avatar';
import { SearchInput } from '../components/SearchInput';
import ServiceTimeline from '../components/ServiceTimeline';

function toDateStr(d) {
    return d.toISOString().split('T')[0];
}

function getPresetRange(preset) {
    const now = new Date();
    const end = toDateStr(now);
    const d = new Date();
    switch (preset) {
        case 'week':   d.setDate(d.getDate() - 7); break;
        case 'month':  d.setMonth(d.getMonth() - 1); break;
        case 'quarter':d.setMonth(d.getMonth() - 3); break;
        case 'year':   d.setFullYear(d.getFullYear() - 1); break;
        default: return null;
    }
    return { start: toDateStr(d), end };
}

function defaultStartDate() {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return toDateStr(d);
}

function defaultEndDate() {
    return toDateStr(new Date());
}

function rateNumClasses(rate) {
    if (rate >= 80) return 'text-emerald-600 dark:text-emerald-400';
    if (rate >= 50) return 'text-amber-600 dark:text-amber-400';
    return 'text-red-600 dark:text-red-400';
}

function rateBarColor(rate) {
    if (rate >= 80) return '#059669';
    if (rate >= 50) return '#d97706';
    return '#dc2626';
}

export function AttendanceReports() {
    const { isAuthenticated, isAdmin } = useAuth();
    const navigate = useNavigate();

    const [startDate, setStartDate] = useState(defaultStartDate);
    const [endDate, setEndDate] = useState(defaultEndDate);
    const [activePreset, setActivePreset] = useState('month');
    const [selectedRegion, setSelectedRegion] = useState(null);
    const [regions, setRegions] = useState([]);
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Member table controls
    const [memberSearch, setMemberSearch] = useState('');
    const [sortCol, setSortCol] = useState('weekly');
    const [sortDir, setSortDir] = useState('desc');
    const [showChart, setShowChart] = useState(true);
    const [leadersOnly, setLeadersOnly] = useState(false);

    useEffect(() => {
        if (!isAuthenticated) return;
        regionAPI.getRegions()
            .then((res) => setRegions((res.data.data || {}).items || []))
            .catch(() => {});
    }, [isAuthenticated]);

    const handlePreset = (preset) => {
        const range = getPresetRange(preset);
        if (range) {
            setStartDate(range.start);
            setEndDate(range.end);
            setActivePreset(preset);
            setSelectedRegion(null);
        }
    };

    const handleDateChange = (setter) => (e) => {
        setter(e.target.value);
        setActivePreset(null);
        setSelectedRegion(null);
    };

    const fetchReport = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {};
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;
            if (leadersOnly) params.leaders_only = true;
            const res = await serviceAPI.getReport(params);
            setReport(res.data.data || null);
        } catch (err) {
            if (err.response?.status === 403) {
                setError('Only pastors and admins can view reports');
            } else {
                setError(err.response?.data?.message || 'Failed to load report');
            }
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate, leadersOnly]);

    useEffect(() => {
        if (isAuthenticated) fetchReport();
    }, [isAuthenticated, fetchReport]);

    // Compute trend from weekly stats
    const trend = useMemo(() => {
        if (!report?.weekly_stats || report.weekly_stats.length < 2) return null;
        const stats = report.weekly_stats;
        const mid = Math.floor(stats.length / 2);
        const firstHalf = stats.slice(0, mid);
        const secondHalf = stats.slice(mid);
        const avg = (arr) => arr.reduce((s, x) => s + x.attendance_rate, 0) / arr.length;
        const firstAvg = avg(firstHalf);
        const secondAvg = avg(secondHalf);
        const diff = secondAvg - firstAvg;
        return { diff, direction: diff > 1 ? 'up' : diff < -1 ? 'down' : 'flat' };
    }, [report]);

    // Derive extra stats (using weekly rate)
    const extraStats = useMemo(() => {
        if (!report?.members || report.members.length === 0) return null;
        const atRisk = report.members.filter((m) => m.weekly_rate < 50).length;
        const perfect = report.members.filter((m) => m.weekly_rate === 100).length;
        return { atRisk, perfect };
    }, [report]);

    // Sort + filter + group members
    const handleSort = (col) => {
        if (sortCol === col) {
            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortCol(col);
            setSortDir(col === 'name' ? 'asc' : 'desc');
        }
    };

    const filteredSortedMembers = useMemo(() => {
        if (!report?.members) return [];
        let list = [...report.members];
        if (memberSearch) {
            const q = memberSearch.toLowerCase();
            list = list.filter((m) => m.member_name.toLowerCase().includes(q));
        }
        list.sort((a, b) => {
            let cmp = 0;
            switch (sortCol) {
                case 'name': cmp = a.member_name.localeCompare(b.member_name); break;
                case 'present': cmp = a.times_present - b.times_present; break;
                case 'absent': cmp = a.times_absent - b.times_absent; break;
                case 'rate': cmp = a.attendance_rate - b.attendance_rate; break;
                case 'weekly': cmp = a.weekly_rate - b.weekly_rate; break;
                default: cmp = 0;
            }
            return sortDir === 'asc' ? cmp : -cmp;
        });
        return list;
    }, [report, memberSearch, sortCol, sortDir]);

    const regionAggregates = useMemo(() => {
        if (!report?.members || report.members.length === 0) return [];
        const groups = {};
        for (const m of report.members) {
            const rid = m.region_id || '__unassigned';
            if (!groups[rid]) groups[rid] = [];
            groups[rid].push(m);
        }
        return Object.entries(groups)
            .map(([rid, members]) => {
                const avgRate = members.reduce((s, m) => s + m.weekly_rate, 0) / members.length;
                const atRisk = members.filter((m) => m.weekly_rate < 50).length;
                const perfect = members.filter((m) => m.weekly_rate === 100).length;
                const region = regions.find((r) => r.id === rid);
                return {
                    regionId: rid,
                    name: region ? region.name : 'Unassigned',
                    color: region?.color || '#94a3b8',
                    memberCount: members.length,
                    avgRate,
                    atRisk,
                    perfect,
                };
            })
            .sort((a, b) => {
                if (a.regionId === '__unassigned') return 1;
                if (b.regionId === '__unassigned') return -1;
                return a.name.localeCompare(b.name);
            });
    }, [report, regions]);

    const drilldownMembers = useMemo(() => {
        if (selectedRegion === null) return [];
        if (selectedRegion === '__all' || selectedRegion === '__leaders') return filteredSortedMembers;
        return filteredSortedMembers.filter((m) => (m.region_id || '__unassigned') === selectedRegion);
    }, [filteredSortedMembers, selectedRegion]);

    const drilldownStats = useMemo(() => {
        if (selectedRegion === null || drilldownMembers.length === 0) return null;
        const avgRate = drilldownMembers.reduce((s, m) => s + m.weekly_rate, 0) / drilldownMembers.length;
        const atRisk = drilldownMembers.filter((m) => m.weekly_rate < 50).length;
        const perfect = drilldownMembers.filter((m) => m.weekly_rate === 100).length;
        return { memberCount: drilldownMembers.length, avgRate, atRisk, perfect };
    }, [selectedRegion, drilldownMembers]);

    const downloadCSV = () => {
        if (!report?.members) return;
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const header = ['Service Date', 'Day of Week', 'Name', 'Reason'];
        const rows = [];
        for (const m of report.members) {
            for (const svc of (m.service_attendance || [])) {
                if (!svc.present) {
                    const [y, mo, d] = svc.date.split('-').map(Number);
                    const dayName = dayNames[new Date(y, mo - 1, d).getDay()];
                    const escape = (v) => `"${(v || '').replace(/"/g, '""')}"`;
                    rows.push([svc.date, dayName, escape(m.member_name), escape(svc.reason || '')]);
                }
            }
        }
        rows.sort((a, b) => a[0].localeCompare(b[0]) || a[2].localeCompare(b[2]));
        const csv = [header, ...rows].map((r) => r.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `absences_${startDate}_${endDate}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (!isAuthenticated) {
        return (
            <div className="p-4 lg:p-6 max-w-content mx-auto animate-fade-in">
                <Card>
                    <CardBody>
                        <h2>Not authenticated</h2>
                        <p>Please login to view reports.</p>
                        <Button onClick={() => navigate('/login')}>Login</Button>
                    </CardBody>
                </Card>
            </div>
        );
    }

    const presets = [
        { key: 'week', label: 'Week' },
        { key: 'month', label: 'Month' },
        { key: 'quarter', label: 'Quarter' },
        { key: 'year', label: 'Year' },
    ];

    const sortIcon = (col) => {
        if (sortCol !== col) return '';
        return sortDir === 'asc' ? ' \u25B2' : ' \u25BC';
    };

    return (
        <div className="p-4 lg:p-6 max-w-content mx-auto animate-fade-in">
            <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                <div>
                    <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100">Attendance Reports</h1>
                    <p className="text-sm text-slate-500 mt-0.5">View {leadersOnly ? 'leader' : 'member'} attendance statistics</p>
                </div>
                <div className="flex gap-2">
                    {report && <Button variant="secondary" onClick={downloadCSV}>Download CSV</Button>}
                    <Button variant="secondary" onClick={() => navigate('/dashboard')}>Back</Button>
                </div>
            </header>

            <main>
                {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}

                {/* Toolbar */}
                <div className="card-base p-3 mb-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex gap-1 justify-center sm:justify-start">
                            {presets.map((p) => (
                                <button
                                    key={p.key}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                        activePreset === p.key
                                            ? 'bg-accent-500 text-white'
                                            : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                                    }`}
                                    onClick={() => handlePreset(p.key)}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                        <div className="flex items-center gap-2 justify-center sm:justify-end">
                            <input
                                type="date"
                                value={startDate}
                                onChange={handleDateChange(setStartDate)}
                                className="input-field !w-[136px]"
                            />
                            <span className="text-xs text-slate-400">to</span>
                            <input
                                type="date"
                                value={endDate}
                                onChange={handleDateChange(setEndDate)}
                                className="input-field !w-[136px]"
                            />
                        </div>
                    </div>
                </div>

                {loading ? (
                    <p className="text-center text-slate-500 py-8">Loading report...</p>
                ) : report ? (
                    <>
                        {/* Breadcrumb (detail mode) */}
                        {selectedRegion !== null && (
                            <div className="flex items-center gap-1.5 text-sm mb-3">
                                <button onClick={() => { setLeadersOnly(false); setSelectedRegion(null); }} className="text-accent-500 hover:text-accent-600 font-medium">
                                    All Regions
                                </button>
                                <span className="text-slate-400">&gt;</span>
                                <span className="text-slate-700 dark:text-slate-200 font-medium">
                                    {selectedRegion === '__leaders' ? 'Leaders' : selectedRegion === '__all' ? 'All' : (regionAggregates.find((a) => a.regionId === selectedRegion)?.name || 'Region')}
                                </span>
                            </div>
                        )}

                        {/* Stats strip */}
                        {selectedRegion === null ? (
                            <div className="flex flex-wrap gap-2 mb-4">
                                <div className="card-base px-3 py-1.5 text-center">
                                    <span className="text-base font-bold text-slate-800 dark:text-slate-100 block">{report.total_weeks}</span>
                                    <span className="text-[10px] text-slate-500 uppercase tracking-wide">Weeks</span>
                                </div>
                                <div className="card-base px-3 py-1.5 text-center !border-accent-200 dark:!border-accent-800">
                                    <span className="text-base font-bold text-slate-800 dark:text-slate-100 flex items-center justify-center gap-1">
                                        {report.avg_weekly_rate.toFixed(1)}%
                                        {trend && trend.direction !== 'flat' && (
                                            <span className={trend.direction === 'up' ? 'text-emerald-500' : 'text-red-500'}>
                                                {trend.direction === 'up' ? '\u2191' : '\u2193'}
                                            </span>
                                        )}
                                    </span>
                                    <span className="text-[10px] text-slate-500 uppercase tracking-wide">Weekly Rate</span>
                                </div>
                                <div className="card-base px-3 py-1.5 text-center">
                                    <span className="text-base font-bold text-slate-800 dark:text-slate-100 block">{report.total_services}</span>
                                    <span className="text-[10px] text-slate-500 uppercase tracking-wide">Services</span>
                                </div>
                                <div className="card-base px-3 py-1.5 text-center">
                                    <span className="text-base font-bold text-slate-800 dark:text-slate-100 block">{report.members.length}</span>
                                    <span className="text-[10px] text-slate-500 uppercase tracking-wide">{leadersOnly ? 'Leaders' : 'Members'}</span>
                                </div>
                                {extraStats && extraStats.perfect > 0 && (
                                    <div className="card-base px-3 py-1.5 text-center">
                                        <span className="text-base font-bold text-emerald-500 block">{extraStats.perfect}</span>
                                        <span className="text-[10px] text-slate-500 uppercase tracking-wide">Perfect</span>
                                    </div>
                                )}
                                {extraStats && extraStats.atRisk > 0 && (
                                    <div className="card-base px-3 py-1.5 text-center">
                                        <span className="text-base font-bold text-red-500 block">{extraStats.atRisk}</span>
                                        <span className="text-[10px] text-slate-500 uppercase tracking-wide">At Risk</span>
                                    </div>
                                )}
                            </div>
                        ) : drilldownStats && (
                            <div className="flex flex-wrap gap-2 mb-4">
                                <div className="card-base px-3 py-1.5 text-center">
                                    <span className="text-base font-bold text-slate-800 dark:text-slate-100 block">{report.total_weeks}</span>
                                    <span className="text-[10px] text-slate-500 uppercase tracking-wide">Weeks</span>
                                </div>
                                <div className="card-base px-3 py-1.5 text-center">
                                    <span className="text-base font-bold text-slate-800 dark:text-slate-100 block">{drilldownStats.memberCount}</span>
                                    <span className="text-[10px] text-slate-500 uppercase tracking-wide">{leadersOnly ? 'Leaders' : 'Members'}</span>
                                </div>
                                <div className="card-base px-3 py-1.5 text-center !border-accent-200 dark:!border-accent-800">
                                    <span className="text-base font-bold text-slate-800 dark:text-slate-100 block">{drilldownStats.avgRate.toFixed(1)}%</span>
                                    <span className="text-[10px] text-slate-500 uppercase tracking-wide">Weekly Rate</span>
                                </div>
                                {drilldownStats.perfect > 0 && (
                                    <div className="card-base px-3 py-1.5 text-center">
                                        <span className="text-base font-bold text-emerald-500 block">{drilldownStats.perfect}</span>
                                        <span className="text-[10px] text-slate-500 uppercase tracking-wide">Perfect</span>
                                    </div>
                                )}
                                {drilldownStats.atRisk > 0 && (
                                    <div className="card-base px-3 py-1.5 text-center">
                                        <span className="text-base font-bold text-red-500 block">{drilldownStats.atRisk}</span>
                                        <span className="text-[10px] text-slate-500 uppercase tracking-wide">At Risk</span>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Chart */}
                        {report.weekly_stats && report.weekly_stats.length > 0 && (
                            <Card className="mb-4">
                                <CardHeader>
                                    <div className="flex items-center justify-between w-full">
                                        <h2 className="text-base font-semibold text-slate-800 dark:text-slate-100">Weekly Attendance Trend</h2>
                                        <button className="text-xs text-accent-500 hover:text-accent-600 font-medium" onClick={() => setShowChart(!showChart)}>
                                            {showChart ? 'Hide' : 'Show'}
                                        </button>
                                    </div>
                                </CardHeader>
                                {showChart && (
                                    <CardBody>
                                        <div className="w-full">
                                            <ResponsiveContainer width="100%" height={220}>
                                                <LineChart data={report.weekly_stats.map((w) => ({
                                                    ...w,
                                                    label: `Week of ${w.week_start}`,
                                                }))}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                                                    <XAxis dataKey="label" tick={{ fontSize: 10 }} angle={-35} textAnchor="end" height={55} />
                                                    <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} width={40} />
                                                    <Tooltip
                                                        formatter={(value) => [`${value.toFixed(1)}%`, 'Weekly Attendance']}
                                                        labelFormatter={(label) => label}
                                                    />
                                                    <Line type="monotone" dataKey="attendance_rate" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} name="Weekly Attendance" />
                                                </LineChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </CardBody>
                                )}
                            </Card>
                        )}

                        {/* Overview: Region cards */}
                        {selectedRegion === null ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                {regionAggregates.length > 1 && (
                                    <button
                                        onClick={() => setSelectedRegion('__all')}
                                        className="card-base p-3 text-left hover:shadow-md transition-shadow"
                                        style={{ borderLeftWidth: '4px', borderLeftColor: '#3b82f6' }}
                                    >
                                        <div className="font-semibold text-sm text-slate-800 dark:text-slate-100 mb-1">All</div>
                                        <div className={`text-xl font-bold mb-0.5 ${rateNumClasses(report.avg_weekly_rate)}`}>
                                            {report.avg_weekly_rate.toFixed(0)}%
                                        </div>
                                        <div className="flex items-center gap-3 text-xs text-slate-500">
                                            <span>{report.members.length} member{report.members.length !== 1 ? 's' : ''}</span>
                                            {extraStats?.atRisk > 0 && (
                                                <span className="text-red-500 font-medium">{extraStats.atRisk} at risk</span>
                                            )}
                                        </div>
                                    </button>
                                )}
                                {regionAggregates.map((agg) => (
                                    <button
                                        key={agg.regionId}
                                        onClick={() => setSelectedRegion(agg.regionId)}
                                        className="card-base p-3 text-left hover:shadow-md transition-shadow"
                                        style={{ borderLeftWidth: '4px', borderLeftColor: agg.color }}
                                    >
                                        <div className="font-semibold text-sm text-slate-800 dark:text-slate-100 mb-1">{agg.name}</div>
                                        <div className={`text-xl font-bold mb-0.5 ${rateNumClasses(agg.avgRate)}`}>
                                            {agg.avgRate.toFixed(0)}%
                                        </div>
                                        <div className="flex items-center gap-3 text-xs text-slate-500">
                                            <span>{agg.memberCount} member{agg.memberCount !== 1 ? 's' : ''}</span>
                                            {agg.atRisk > 0 && (
                                                <span className="text-red-500 font-medium">{agg.atRisk} at risk</span>
                                            )}
                                        </div>
                                    </button>
                                ))}
                                {isAdmin && (
                                    <button
                                        onClick={() => { setLeadersOnly(true); setSelectedRegion('__leaders'); }}
                                        className="card-base p-3 text-left hover:shadow-md transition-shadow"
                                        style={{ borderLeftWidth: '4px', borderLeftColor: '#9333ea' }}
                                    >
                                        <div className="font-semibold text-sm text-slate-800 dark:text-slate-100 mb-1">Leaders</div>
                                        <div className="text-xs text-slate-500 mt-1">View leader attendance</div>
                                    </button>
                                )}
                            </div>
                        ) : (
                            /* Detail: Member table */
                            <Card>
                                <CardHeader>
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 w-full">
                                        <h2 className="text-base font-semibold text-slate-800 dark:text-slate-100 whitespace-nowrap">{leadersOnly ? 'Leaders' : 'Members'} ({drilldownMembers.length})</h2>
                                        <SearchInput
                                            value={memberSearch}
                                            onChange={(e) => setMemberSearch(e.target.value)}
                                            placeholder={leadersOnly ? 'Search leaders...' : 'Search members...'}
                                            maxWidth="200px"
                                        />
                                    </div>
                                </CardHeader>
                                <CardBody style={{ padding: 0 }}>
                                    {drilldownMembers.length === 0 ? (
                                        <p className="text-center text-slate-500 py-4 px-4">
                                            {memberSearch ? `No ${leadersOnly ? 'leaders' : 'members'} match your search` : `No ${leadersOnly ? 'leaders' : 'members'} found for this period`}
                                        </p>
                                    ) : (
                                        <div className="overflow-x-auto">
                                            {/* Desktop table */}
                                            <table className="w-full border-collapse hidden sm:table">
                                                <thead className="sticky top-0 z-[2] bg-white dark:bg-slate-800">
                                                    <tr>
                                                        <th className="cursor-pointer select-none hover:text-slate-700 dark:hover:text-slate-200 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide px-3 py-2 border-b-2 border-slate-200 dark:border-slate-700 whitespace-nowrap" onClick={() => handleSort('name')}>
                                                            Name{sortIcon('name')}
                                                        </th>
                                                        <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide px-3 py-2 border-b-2 border-slate-200 dark:border-slate-700 whitespace-nowrap">
                                                            Timeline
                                                        </th>
                                                        <th className="cursor-pointer select-none hover:text-slate-700 dark:hover:text-slate-200 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide px-3 py-2 border-b-2 border-slate-200 dark:border-slate-700 whitespace-nowrap min-w-[60px]" onClick={() => handleSort('weekly')}>
                                                            Weeks{sortIcon('weekly')}
                                                        </th>
                                                        <th className="cursor-pointer select-none hover:text-slate-700 dark:hover:text-slate-200 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide px-3 py-2 border-b-2 border-slate-200 dark:border-slate-700 whitespace-nowrap min-w-[60px]" onClick={() => handleSort('present')}>
                                                            Services{sortIcon('present')}
                                                        </th>
                                                        <th className="cursor-pointer select-none hover:text-slate-700 dark:hover:text-slate-200 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide px-3 py-2 border-b-2 border-slate-200 dark:border-slate-700 whitespace-nowrap min-w-[140px]" onClick={() => handleSort('weekly')}>
                                                            Weekly Rate{sortIcon('weekly')}
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {drilldownMembers.map((m) => (
                                                        <tr key={m.member_id} className={`border-b border-slate-100 dark:border-slate-700 last:border-b-0 ${m.weekly_rate < 50 ? 'bg-red-50/50 dark:bg-red-950/20' : ''}`}>
                                                            <td className="px-3 py-1.5 text-sm font-medium whitespace-nowrap">
                                                                <div className="flex items-center gap-1.5">
                                                                    <Avatar name={m.member_name} photoUrl={m.photo_url} size="sm" /> {m.member_name}
                                                                </div>
                                                            </td>
                                                            <td className="px-3 py-1.5">
                                                                <ServiceTimeline services={m.service_attendance} />
                                                            </td>
                                                            <td className="px-3 py-1.5 text-sm text-right min-w-[60px]">{m.weeks_attended}/{m.total_weeks}</td>
                                                            <td className="px-3 py-1.5 text-sm text-right min-w-[60px]">{m.times_present}/{m.total_services}</td>
                                                            <td className="px-3 py-1.5 min-w-[140px]">
                                                                <div className="flex items-center gap-2">
                                                                    <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded-full flex-1 overflow-hidden min-w-[50px]">
                                                                        <div
                                                                            className="h-full rounded-full transition-all"
                                                                            style={{
                                                                                width: `${Math.min(m.weekly_rate, 100)}%`,
                                                                                background: rateBarColor(m.weekly_rate),
                                                                            }}
                                                                        />
                                                                    </div>
                                                                    <span className={`text-xs font-semibold min-w-[2.5rem] text-right ${rateNumClasses(m.weekly_rate)}`}>
                                                                        {m.weekly_rate.toFixed(0)}%
                                                                    </span>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>

                                            {/* Mobile cards */}
                                            <div className="sm:hidden px-3 py-2">
                                                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-200 dark:border-slate-700 text-xs text-slate-400">
                                                    <span>Sort:</span>
                                                    {[['name','Name'],['weekly','Rate'],['absent','Absent']].map(([k, l]) => (
                                                        <button
                                                            key={k}
                                                            className={`px-2 py-1 rounded text-xs transition-colors ${
                                                                sortCol === k
                                                                    ? 'bg-accent-500 text-white'
                                                                    : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
                                                            }`}
                                                            onClick={() => handleSort(k)}
                                                        >
                                                            {l}{sortCol === k ? (sortDir === 'asc' ? ' \u25B2' : ' \u25BC') : ''}
                                                        </button>
                                                    ))}
                                                </div>
                                                {drilldownMembers.map((m) => (
                                                    <div key={m.member_id} className={`card-base p-3 mb-2 ${m.weekly_rate < 50 ? 'bg-red-50/50 dark:bg-red-950/20' : ''}`}>
                                                        <div className="flex items-center gap-1.5 text-sm font-medium mb-1">
                                                            <Avatar name={m.member_name} photoUrl={m.photo_url} size="sm" /> {m.member_name}
                                                        </div>
                                                        <div className="mb-1.5">
                                                            <ServiceTimeline services={m.service_attendance} compact />
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <div className="h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full flex-1 overflow-hidden min-w-[40px]">
                                                                <div
                                                                    className="h-full rounded-full transition-all"
                                                                    style={{
                                                                        width: `${Math.min(m.weekly_rate, 100)}%`,
                                                                        background: rateBarColor(m.weekly_rate),
                                                                    }}
                                                                />
                                                            </div>
                                                            <span className="text-xs text-slate-400 whitespace-nowrap">
                                                                {m.weeks_attended}/{m.total_weeks} wks
                                                            </span>
                                                            <span className={`text-xs font-semibold min-w-[2.5rem] text-right ${rateNumClasses(m.weekly_rate)}`}>
                                                                {m.weekly_rate.toFixed(0)}%
                                                            </span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        )}
                    </>
                ) : null}
            </main>
        </div>
    );
}

export default AttendanceReports;
